import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $insertNodes, $getSelection, $createTextNode, $getRoot, $setSelection, BaseSelection, $createParagraphNode, ElementNode } from 'lexical';
import styles from '../CustomRichText.module.scss';

interface InsertVariablePluginProps {
  handleOpenVariables: () => void;
  variableSelected?: string;
}

const InsertVariablePlugin: React.FC<InsertVariablePluginProps> = (props) => {
  const { handleOpenVariables, variableSelected } = props;
  const [editor] = useLexicalComposerContext();
  const lastSelectionRef = useRef<BaseSelection | null>(null);
  const { t } = useTranslation();

  const onHandleOpenVariables = () => { // Save the current selection before it loses focus
    editor.getEditorState().read(() => {
      const selection = $getSelection();

      if (selection) {
        lastSelectionRef.current = selection.clone();
      } else {
        lastSelectionRef.current = null;
      }
    });

    handleOpenVariables();
  };

  const insertVariable = (variableName: string) => {
    editor.update(() => {
      const variableText = `{{${variableName}}}`;
      const textNode = $createTextNode(variableText);

      if (lastSelectionRef.current) { // Restore the last valid selection
        $setSelection(lastSelectionRef.current?.clone());
        $insertNodes([textNode]);
      } else { // No selection: insert at the end of the document
        const root = $getRoot();
        const lastNode = root.getLastChild();

        if (lastNode && lastNode instanceof ElementNode) {
          const lastChild = lastNode.getLastChild();

          if (lastChild) lastChild.insertAfter(textNode);
          else lastNode.append(textNode);
        } else {
          const fallbackParagraph = $createParagraphNode();
          fallbackParagraph.append(textNode);
          root.append(fallbackParagraph);
        }
      }
    });
  };

  useEffect(() => {
    if (variableSelected) {
      insertVariable(variableSelected);
    }
  }, [variableSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <button onClick={onHandleOpenVariables} className={styles.VariablesButton}>
      {`${t('dialog.addVariables')}`}
    </button>
  );
};

export default InsertVariablePlugin;
